/**
 * Date range preset buttons replacing manual date inputs.
 * Supports quick presets (7d, 30d, 90d, 1y) and a custom date range.
 */

import { useState } from "react";
import { t } from "../i18n";

type DateRange = {
  readonly start_date: string;
  readonly end_date: string;
};

type Props = {
  readonly value: DateRange;
  readonly onChange: (range: DateRange) => void;
};

type PresetKey = "7d" | "30d" | "90d" | "1y" | "custom";

const PRESET_DAYS: ReadonlyArray<{
  readonly key: PresetKey;
  readonly days: number;
}> = [
  { key: "7d", days: 7 },
  { key: "30d", days: 30 },
  { key: "90d", days: 90 },
  { key: "1y", days: 365 },
];

function toISODate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function makeRange(days: number): DateRange {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return { start_date: toISODate(start), end_date: toISODate(end) };
}

function detectPreset(range: DateRange): PresetKey {
  const today = toISODate(new Date());
  if (range.end_date !== today) return "custom";

  const diffMs =
    new Date(range.end_date).getTime() - new Date(range.start_date).getTime();
  const diffDays = Math.round(diffMs / 86_400_000);

  for (const preset of PRESET_DAYS) {
    if (diffDays === preset.days) return preset.key;
  }
  return "custom";
}

export function DatePresets({ value, onChange }: Props): React.JSX.Element {
  const [active, setActive] = useState<PresetKey>(() => detectPreset(value));

  function handlePreset(key: PresetKey, days: number): void {
    setActive(key);
    onChange(makeRange(days));
  }

  function handleCustom(): void {
    setActive("custom");
  }

  function handleInputChange(
    field: "start_date" | "end_date",
    val: string,
  ): void {
    onChange({ ...value, [field]: val });
  }

  return (
    <div style={s.container}>
      {PRESET_DAYS.map((p) => (
        <button
          key={p.key}
          type="button"
          style={active === p.key ? s.btnActive : s.btn}
          onClick={() => handlePreset(p.key, p.days)}
        >
          {t(`date_picker.${p.key}`)}
        </button>
      ))}
      <button
        type="button"
        style={active === "custom" ? s.btnActive : s.btn}
        onClick={handleCustom}
      >
        {t("date_picker.custom")}
      </button>

      {active === "custom" && (
        <div style={s.inputs}>
          <input
            type="date"
            value={value.start_date}
            onChange={(e) => handleInputChange("start_date", e.target.value)}
            style={s.input}
          />
          <input
            type="date"
            value={value.end_date}
            onChange={(e) => handleInputChange("end_date", e.target.value)}
            style={s.input}
          />
        </div>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  btn: {
    padding: "4px 12px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
    cursor: "pointer",
  },
  btnActive: {
    padding: "4px 12px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    border: "1px solid var(--color-primary)",
    background: "var(--color-primary)",
    color: "#ffffff",
    cursor: "pointer",
  },
  inputs: { display: "flex", gap: 6, marginLeft: 4 },
  input: {
    padding: "4px 8px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
  },
};
